import React, { useEffect, useState } from "react";
import moment from "moment";
import { Button } from "@progress/kendo-react-buttons";
import { useServices } from "src/services";
import { useStores } from "src/stores";
import { DealerFeedback } from "src/utils/classes";

// D4URX-2752: Dealer Feedback for one event. Replaces the per-dealer Dealer Notes on the Event
// page: feedback belongs to this event only, so it never shows on the dealer's other events or
// carries into later years. Timestamp and author show here but are left out of the export.

const fontSemiBold = { fontWeight: 600 };
const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: 8,
  boxSizing: "border-box",
  fontFamily: "inherit",
  fontSize: 14,
  border: "1px solid #ccc",
  borderRadius: 4,
};

interface Props {
  eventId: number;
  feedback: DealerFeedback[];
}

const DealerFeedbackSection = ({ eventId, feedback }: Props) => {
  const { d4ur } = useServices();
  const { login } = useStores();

  const [entries, setEntries] = useState<DealerFeedback[]>(feedback);
  const [note, setNote] = useState<string>("");
  const [contact, setContact] = useState<string>("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNote, setEditNote] = useState<string>("");
  const [editContact, setEditContact] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    setEntries(feedback);
  }, [feedback]);

  const failed = (ret: any, fallback: string) => {
    setError(ret?.result?.message || fallback);
    setSaving(false);
  };

  const handleAdd = async () => {
    if (!note.trim()) return;
    setSaving(true);
    setError("");
    const ret: any = await d4ur.addDealerFeedback(eventId, note.trim(), contact.trim(), login.user?.credentials.display_name || "");
    if (ret?.response?.status !== 200) return failed(ret, "Failed to add feedback.");
    setEntries((prev) => [...prev, ret.result as DealerFeedback]);
    setNote("");
    setContact("");
    setSaving(false);
  };

  const startEdit = (entry: DealerFeedback) => {
    setEditingId(entry.id);
    setEditNote(entry.note);
    setEditContact(entry.contact || "");
    setError("");
  };

  const handleUpdate = async () => {
    if (!editingId || !editNote.trim()) return;
    setSaving(true);
    setError("");
    const ret: any = await d4ur.updateDealerFeedback(eventId, editingId, editNote.trim(), editContact.trim());
    if (ret?.response?.status !== 200) return failed(ret, "Failed to update feedback.");
    setEntries((prev) => prev.map((entry) => (entry.id === editingId ? (ret.result as DealerFeedback) : entry)));
    setEditingId(null);
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this feedback note?")) return;
    setSaving(true);
    setError("");
    const ret: any = await d4ur.deleteDealerFeedback(eventId, id);
    if (ret?.response?.status !== 200) return failed(ret, "Failed to delete feedback.");
    setEntries((prev) => prev.filter((entry) => entry.id !== id));
    setSaving(false);
  };

  return (
    <section style={{ marginBottom: 10 }}>
      {entries.length === 0 && <p style={{ color: "#666", marginTop: 0 }}>No feedback yet.</p>}

      {entries.map((entry) => (
        <div key={entry.id} style={{ borderBottom: "1px solid lightgrey", padding: "6px 0" }}>
          {editingId === entry.id ? (
            <>
              <textarea style={{ ...inputStyle, minHeight: 60 }} maxLength={2000} value={editNote} onChange={(e) => setEditNote(e.target.value)} />
              <input style={{ ...inputStyle, marginTop: 6 }} maxLength={255} placeholder="Feedback contact (optional)" value={editContact} onChange={(e) => setEditContact(e.target.value)} />
              <div style={{ marginTop: 6 }}>
                <Button className="ford-blue-btn" disabled={saving || !editNote.trim()} onClick={handleUpdate}>Save</Button>
                &nbsp;
                <Button disabled={saving} onClick={() => setEditingId(null)}>Cancel</Button>
              </div>
            </>
          ) : (
            <>
              <div style={{ whiteSpace: "pre-wrap" }}>{entry.note}</div>
              <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
                <span style={fontSemiBold}>Contact: </span>{entry.contact || "N/A"}
                {" · "}
                {moment(entry.created_at).format("MMM DD, YYYY hh:mm A")} - {entry.created_by_name || entry.created_by}
                {entry.updated_at && <> (edited {moment(entry.updated_at).format("MMM DD, YYYY")})</>}
              </div>
              <div style={{ marginTop: 4 }}>
                <Button size="small" disabled={saving} onClick={() => startEdit(entry)}>Edit</Button>
                &nbsp;
                <Button size="small" disabled={saving} onClick={() => handleDelete(entry.id)}>Delete</Button>
              </div>
            </>
          )}
        </div>
      ))}

      <div style={{ marginTop: 10 }}>
        <textarea style={{ ...inputStyle, minHeight: 60 }} maxLength={2000} placeholder="Feedback note" value={note} onChange={(e) => setNote(e.target.value)} />
        <input style={{ ...inputStyle, marginTop: 6 }} maxLength={255} placeholder="Feedback contact (optional)" value={contact} onChange={(e) => setContact(e.target.value)} />
        <Button className="ford-blue-btn" style={{ marginTop: 6 }} disabled={saving || !note.trim()} onClick={handleAdd}>
          Add Feedback
        </Button>
      </div>
      {error && <p style={{ color: "red" }}>{error}</p>}
    </section>
  );
};

export default DealerFeedbackSection;
